import { useState, useRef } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Loader2, ListChecks, AlertTriangle, Play, Eye, RefreshCw, Download, Archive, Stethoscope, FileText, Upload, Copy, Wrench, Hash } from "lucide-react";

export default function SchemaTipiMigrazione() {
  const [prev, setPrev] = useState(null);
  const [busy, setBusy] = useState("");
  const [varianti, setVarianti] = useState({});
  const [manuali, setManuali] = useState({});

  const anteprima = async () => {
    setBusy("prev");
    try { setPrev((await api.get("/admin/schema-tipi/anteprima")).data); }
    catch (e) { toast.error(e?.response?.data?.detail || "Anteprima non disponibile"); }
    finally { setBusy(""); }
  };
  const catalogo = async () => {
    setBusy("cat");
    try {
      const { data } = await api.post("/admin/schema-tipi/catalogo", {});
      toast.success(`Catalogo aggiornato: ${data.tipi_creati} tipi creati, ${data.tipi_aggiornati} aggiornati (${data.tipi_totali} totali) · nessuna pratica modificata`);
      if (prev) await anteprima();
    } catch (e) { toast.error(e?.response?.data?.detail || "Aggiornamento catalogo non riuscito"); }
    finally { setBusy(""); }
  };

  const [diag, setDiag] = useState(null);

  const fileContenuti = useRef(null);

  const contenuti = async (payload) => {
    setBusy("cont");
    try {
      const { data } = await api.post("/admin/schema-tipi/contenuti", payload || {});
      toast.success(`Contenuti caricati su ${data.aggiornati} tipi (${data.origine})${data.non_trovati.length ? ` · ${data.non_trovati.length} non trovati` : ""}`);
      if (data.non_trovati.length) toast.error(`Non trovati: ${data.non_trovati.join(", ")}`);
      if (prev) await anteprima();
    } catch (e) { toast.error(e?.response?.data?.detail || "Caricamento contenuti non riuscito"); }
    finally { setBusy(""); }
  };

  const scegliFileContenuti = async (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f) return;
    try {
      const testo = await f.text();
      const json = JSON.parse(testo);
      if (!Array.isArray(json?.tipi)) { toast.error("Il file non contiene la chiave 'tipi'"); return; }
      await contenuti({ contenuti: json });
    } catch { toast.error("File JSON non leggibile"); }
  };

  const [dup, setDup] = useState(null);

  const codici = async (esegui) => {
    setBusy(esegui ? "cod" : "codprev");
    try {
      const { data } = await api.post("/admin/schema-tipi/assegna-codici", { esegui: !!esegui });
      toast.success(esegui
        ? `Codici assegnati a ${data.assegnati.length} tipi (${data.attivi_senza_codice} attivi, ${data.archiviati_senza_codice} archiviati)`
        : `Anteprima: ${data.assegnati.length} tipi da codificare · ${data.gia_con_codice} già a posto`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Assegnazione codici non riuscita"); }
    finally { setBusy(""); }
  };

  const doppioni = async () => {
    setBusy("dup");
    try {
      const { data } = await api.get("/admin/schema-tipi/doppioni");
      setDup(data);
      toast.success(`${data.attivi} tipi attivi · ${data.coppie.length} coppie doppie · ${data.pratiche_orfane.length} pratiche orfane`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Diagnosi doppioni non riuscita"); }
    finally { setBusy(""); }
  };

  const ripara = async (esegui) => {
    if (esegui && !window.confirm("Sposto i contenuti sui tipi con le pratiche, archivio i doppioni e riaggancio le pratiche orfane. Nessun tipo viene eliminato. Procedo?")) return;
    setBusy(esegui ? "rip" : "ripprev");
    try {
      const { data } = await api.post("/admin/schema-tipi/ripara-doppioni", { esegui: !!esegui });
      toast.success(esegui
        ? `Fatto: ${data.tipi_attivi} tipi attivi, ${data.tipi_archiviati} archiviati, ${data.pratiche_orfane} pratiche orfane`
        : `Anteprima: ${data.contenuti_spostati.length} contenuti da spostare, ${data.archiviati.length} da archiviare, ${data.pratiche_riagganciate.length} pratiche da riagganciare`);
      if (data.da_decidere_a_mano.length) toast.error(`${data.da_decidere_a_mano.length} coppie con pratiche su entrambi: da decidere a mano`);
      await doppioni();
    } catch (e) { toast.error(e?.response?.data?.detail || "Riparazione non riuscita"); }
    finally { setBusy(""); }
  };

  const diagnosi = async () => {
    setBusy("diag");
    try {
      const { data } = await api.get("/admin/schema-tipi/diagnosi");
      setDiag(data);
      toast.success(`${data.totale_tipi} tipi nel database (attesi ${data.attesi}) · ${data.coppie.length} coppie sospette`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Diagnosi non riuscita"); }
    finally { setBusy(""); }
  };

  const backup = async () => {
    setBusy("bk");
    try {
      const res = await api.get("/admin/schema-tipi/backup", { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([res.data], { type: "application/json" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `backup-rbstudio-${new Date().toISOString().slice(0, 16).replace(/[:T]/g, "")}.json`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success("Backup scaricato: tipi, pratiche, step, date e note del diario");
    } catch (e) { toast.error(e?.response?.data?.detail || "Backup non riuscito"); }
    finally { setBusy(""); }
  };

  const archiviaTutti = async () => {
    const da = (prev.tipi_da_archiviare || []).filter((t) => !t.archiviato);
    const conPratiche = da.filter((t) => t.pratiche > 0);
    const avviso = conPratiche.length
      ? `\n\nDi questi, ${conPratiche.length} hanno pratiche collegate (${conPratiche.map((t) => `${t.nome}: ${t.pratiche}`).join(", ")}). Le pratiche restano valide: portano i propri step.`
      : "";
    if (!window.confirm(`Archiviare ${da.length} tipi? Nessuna eliminazione: restano nel database ma non saranno più proposti.${avviso}`)) return;
    setBusy("arch");
    try {
      const { data } = await api.post("/admin/schema-tipi/archivia-fuori-catalogo", {});
      toast.success(`${data.archiviati_ora} tipi archiviati · ${data.tipi_attivi} tipi attivi rimasti`);
      await anteprima();
    } catch (e) { toast.error(e?.response?.data?.detail || "Archiviazione non riuscita"); }
    finally { setBusy(""); }
  };

  const archivia = async (t, archivia_ = true) => {
    if (archivia_ && t.pratiche > 0 &&
        !window.confirm(`«${t.nome}» ha ${t.pratiche} pratiche collegate. Archiviare comunque? Le pratiche restano valide e potrai migrarle una a una.`)) return;
    setBusy(t.id);
    try {
      await api.post(`/admin/schema-tipi/archivia-tipo/${t.id}`, { archivia: archivia_, forza: true });
      toast.success(archivia_ ? `«${t.nome}» archiviato` : `«${t.nome}» ripristinato`);
      await anteprima();
    } catch (e) { toast.error(e?.response?.data?.detail || "Operazione non riuscita"); }
    finally { setBusy(""); }
  };

  const esegui = async () => {
    if (!window.confirm("Applicare il nuovo schema e migrare le pratiche mappate? L'operazione è ripetibile senza danni.")) return;
    setBusy("run");
    try {
      const { data } = await api.post("/admin/schema-tipi/esegui", {});
      toast.success(`Tipi: ${data.tipi_creati} creati, ${data.tipi_aggiornati} aggiornati · pratiche migrate: ${data.pratiche_migrate} (saltate ${data.pratiche_saltate})`);
      await anteprima();
    } catch (e) { toast.error(e?.response?.data?.detail || "Migrazione non riuscita"); }
    finally { setBusy(""); }
  };
  const applicaFlussi = async (r) => {
    const v = varianti[r.pratica_id] || "impresa";
    setBusy(r.pratica_id);
    try { await api.post(`/admin/schema-tipi/flussi/${r.pratica_id}`, { variante: v }); toast.success("Pratica Flussi migrata"); await anteprima(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Non riuscita"); }
    finally { setBusy(""); }
  };

  const applicaVistoD = async (r) => {
    const v = varianti[r.pratica_id] || (prev.varianti_visto_d || [])[0];
    setBusy(r.pratica_id);
    try { await api.post(`/admin/schema-tipi/visto-d/${r.pratica_id}`, { variante: v }); toast.success(`Migrata in «${v}»`); await anteprima(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Non riuscita"); }
    finally { setBusy(""); }
  };

  const applicaManuale = async (r) => {
    const m = manuali[r.pratica_id] || {};
    if (!m.tipo || !m.step) { toast.error("Scegli tipo e step attuale"); return; }
    setBusy(r.pratica_id);
    try {
      await api.post(`/admin/schema-tipi/assegna/${r.pratica_id}`, { nuovo_tipo: m.tipo, nuovo_step: m.step });
      toast.success("Pratica assegnata manualmente");
      await anteprima();
    } catch (e) { toast.error(e?.response?.data?.detail || "Non riuscita"); }
    finally { setBusy(""); }
  };

  const th = "text-left text-[10px] uppercase tracking-[0.1em] text-[#8a93a6] font-medium py-1.5 px-2";
  const td = "text-[12px] text-[#1A2B4A] py-1.5 px-2 align-top";

  return (
    <div data-testid="schema-migrazione" className="bg-white border border-[#1A2B4A]/12 rounded-lg p-4 shadow-sm">
      <div className="flex items-center gap-2 mb-1">
        <ListChecks className="w-4 h-4 text-[#C9A84C]" />
        <h3 className="font-serif text-xl text-[#1A2B4A]">Schema tipi pratica (01/09/2026)</h3>
      </div>
      <p className="text-[11px] text-[#4a5568] mb-4">
        Tre fasi, nell'ordine: <b>1)</b> aggiorna il catalogo dei tipi pratica (idempotente, non elimina nulla),
        <b> 2)</b> genera l'anteprima della migrazione (nessuna scrittura), <b>3)</b> esegui la migrazione delle pratiche.
        Prima di eseguire, <b>scarica il backup</b> (tipi, pratiche, step con date e note del diario).
        Le righe senza corrispondenza esatta restano in attesa di assegnazione manuale.
      </p>

      <div className="flex flex-wrap gap-2">
        <button data-testid="schema-catalogo" onClick={catalogo} disabled={!!busy}
          className="inline-flex items-center gap-2 px-3 py-2 text-sm border border-[#C9A84C] bg-[#fdfaf1] rounded-md hover:bg-[#f7efd9] disabled:opacity-60">
          {busy === "cat" ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />} 1. Aggiorna catalogo tipi
        </button>
        <button data-testid="schema-anteprima" onClick={anteprima} disabled={!!busy}
          className="inline-flex items-center gap-2 px-3 py-2 text-sm border border-[#1A2B4A] rounded-md hover:bg-[#f9f8f5] disabled:opacity-60">
          {busy === "prev" ? <Loader2 className="w-4 h-4 animate-spin" /> : <Eye className="w-4 h-4" />} 2. Anteprima migrazione
        </button>
        <button data-testid="schema-contenuti" onClick={() => contenuti()} disabled={!!busy}
          className="inline-flex items-center gap-2 px-3 py-2 text-sm border border-[#C9A84C] bg-[#fdfaf1] rounded-md hover:bg-[#f7efd9] disabled:opacity-60">
          {busy === "cont" ? <Loader2 className="w-4 h-4 animate-spin" /> : <FileText className="w-4 h-4" />} 1-bis. Carica descrizioni e documenti
        </button>
        <input ref={fileContenuti} type="file" accept="application/json,.json" className="hidden"
          data-testid="schema-contenuti-file" onChange={scegliFileContenuti} />
        <button data-testid="schema-contenuti-upload" onClick={() => fileContenuti.current?.click()} disabled={!!busy}
          className="inline-flex items-center gap-2 px-3 py-2 text-sm border border-[#C9A84C]/60 rounded-md hover:bg-[#f7efd9] disabled:opacity-60">
          <Upload className="w-4 h-4" /> 1-bis. Carica da un mio file JSON
        </button>
        <button data-testid="schema-codici-anteprima" onClick={() => codici(false)} disabled={!!busy}
          className="inline-flex items-center gap-2 px-3 py-2 text-sm border border-[#1A2B4A]/40 rounded-md hover:bg-[#f9f8f5] disabled:opacity-60">
          {busy === "codprev" ? <Loader2 className="w-4 h-4 animate-spin" /> : <Eye className="w-4 h-4" />} Anteprima codici
        </button>
        <button data-testid="schema-codici" onClick={() => codici(true)} disabled={!!busy}
          className="inline-flex items-center gap-2 px-3 py-2 text-sm border border-[#1A2B4A] rounded-md hover:bg-[#f9f8f5] disabled:opacity-60">
          {busy === "cod" ? <Loader2 className="w-4 h-4 animate-spin" /> : <Hash className="w-4 h-4" />} 0. Assegna codici ai tipi
        </button>
        <button data-testid="schema-doppioni" onClick={doppioni} disabled={!!busy}
          className="inline-flex items-center gap-2 px-3 py-2 text-sm border border-[#9b2c2c]/40 rounded-md hover:bg-[#fdf3f3] disabled:opacity-60">
          {busy === "dup" ? <Loader2 className="w-4 h-4 animate-spin" /> : <Copy className="w-4 h-4" />} Diagnosi doppioni
        </button>
        <button data-testid="schema-ripara-anteprima" onClick={() => ripara(false)} disabled={!!busy}
          className="inline-flex items-center gap-2 px-3 py-2 text-sm border border-[#9b2c2c]/40 rounded-md hover:bg-[#fdf3f3] disabled:opacity-60">
          {busy === "ripprev" ? <Loader2 className="w-4 h-4 animate-spin" /> : <Eye className="w-4 h-4" />} Anteprima riparazione
        </button>
        <button data-testid="schema-ripara" onClick={() => ripara(true)} disabled={!!busy}
          className="inline-flex items-center gap-2 px-3 py-2 text-sm border border-[#9b2c2c] bg-[#fdf3f3] rounded-md hover:bg-[#f9e6e6] disabled:opacity-60">
          {busy === "rip" ? <Loader2 className="w-4 h-4 animate-spin" /> : <Wrench className="w-4 h-4" />} Ripara doppioni
        </button>
        <button data-testid="schema-diagnosi" onClick={diagnosi} disabled={!!busy}
          className="inline-flex items-center gap-2 px-3 py-2 text-sm border border-[#1A2B4A]/40 rounded-md hover:bg-[#f9f8f5] disabled:opacity-60">
          {busy === "diag" ? <Loader2 className="w-4 h-4 animate-spin" /> : <Stethoscope className="w-4 h-4" />} Diagnosi tipi
        </button>
        <button data-testid="schema-backup" onClick={backup} disabled={!!busy}
          className="inline-flex items-center gap-2 px-3 py-2 text-sm border border-[#1A2B4A]/40 rounded-md hover:bg-[#f9f8f5] disabled:opacity-60">
          {busy === "bk" ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />} Scarica backup
        </button>
        <button data-testid="schema-esegui" onClick={esegui} disabled={!prev || !!busy}
          title={!prev ? "Apri prima l'anteprima" : ""}
          className="inline-flex items-center gap-2 px-3 py-2 text-sm bg-[#1A2B4A] text-white rounded-md font-semibold hover:bg-[#22345c] disabled:opacity-40">
          {busy === "run" ? <Loader2 className="w-4 h-4 animate-spin" /> : <Play className="w-4 h-4" />} 3. Esegui migrazione
        </button>
      </div>

      {dup && (
        <div data-testid="doppioni-panel" className="mt-4 border border-[#9b2c2c]/25 rounded-md p-4 bg-[#fffdfd]">
          <div className="text-sm font-medium text-[#1A2B4A]">
            {dup.totale_tipi} tipi · {dup.attivi} attivi · {dup.archiviati} archiviati · {dup.coppie.length} coppie doppie · {dup.pratiche_orfane.length} pratiche orfane
          </div>
          {dup.coppie.map((c) => (
            <div key={c.codice} data-testid={`coppia-${c.codice}`} className="mt-3 text-[12px] text-[#4a5568]">
              <div className="font-medium text-[#1A2B4A]">{c.codice}</div>
              {c.tipi.map((t) => (
                <div key={t.id}>
                  {t.id.slice(0, 8)} · {t.nome} · {t.pratiche} pratiche · {t.ha_descrizione ? "con descrizione" : "senza descrizione"} · {t.n_documenti} documenti · creato {(t.created_at || "").slice(0, 10)}
                </div>
              ))}
              <div className="text-[#9b2c2c]">{c.azione}</div>
            </div>
          ))}
          {dup.pratiche_orfane.map((o) => (
            <div key={o.pratica_id} data-testid={`orfana-${o.pratica_id}`} className="mt-2 text-[12px] text-[#9b2c2c]">
              Pratica {o.pratica_id.slice(0, 8)} «{o.titolo}» punta a un tipo che non esiste più ({String(o.tipo_id_mancante).slice(0, 8)}) → {o.riaggancio_nome || "nessun tipo corrispondente"}
            </div>
          ))}
          <details className="mt-3 text-[12px]">
            <summary className="cursor-pointer text-[#1A2B4A]">Elenco completo dei tipi</summary>
            <div className="mt-2 space-y-1 text-[#4a5568]">
              {dup.tipi.map((t) => (
                <div key={t.id}>
                  {t.id.slice(0, 8)} · {t.nome} · {t.codice || "senza codice"} · {t.pratiche} pratiche · {t.ha_descrizione ? "desc" : "no desc"} · {t.n_documenti} doc · {t.n_step} step · creato {(t.created_at || "").slice(0, 10)}{t.archiviato ? " · ARCHIVIATO" : ""}
                </div>
              ))}
            </div>
          </details>
        </div>
      )}

      {diag && (
        <div className="mt-5 space-y-4" data-testid="schema-diagnosi-box">
          <div className={`rounded-md border px-3 py-2 ${diag.totale_tipi === diag.attesi ? "border-[#1A2B4A]/15 bg-[#f9f8f5]" : "border-[#9b2c2c]/25 bg-[#fdf3f3]"}`}>
            <p className="text-[12px] text-[#1A2B4A]" data-testid="diagnosi-riepilogo">
              <b>{diag.totale_tipi}</b> tipi nel database · attesi <b>{diag.attesi}</b> ·
              coppie sospette (differiscono solo per punteggiatura, spazi o tipo di trattino): <b>{diag.coppie.length}</b> ·
              fuori catalogo: <b>{diag.fuori_catalogo.length}</b> · del catalogo mancanti: <b>{diag.catalogo_mancanti.length}</b>
            </p>
          </div>

          {diag.coppie.length > 0 && (
            <div>
              <h4 className="text-sm font-semibold uppercase tracking-wide text-[#1A2B4A] mb-2">Coppie sospette</h4>
              <ul className="space-y-2" data-testid="diagnosi-coppie">
                {diag.coppie.map((c) => (
                  <li key={c.chiave} className="border border-[#9b2c2c]/20 rounded-md px-3 py-2 bg-[#fdf9f9]">
                    {c.tipi.map((t) => (
                      <div key={t.id} className="text-[12px] text-[#1A2B4A] flex flex-wrap gap-x-3">
                        <span className="font-medium">{t.nome}</span>
                        <span className={t.pratiche > 0 ? "text-[#9b2c2c] font-semibold" : "text-[#8a93a6]"}>
                          {t.pratiche} pratiche
                        </span>
                        <span className="text-[#8a93a6]">{t.n_step} step · € {t.importo_default ?? "—"} · creato {(t.created_at || "").slice(0, 10)}{t.archiviato ? " · archiviato" : ""}</span>
                      </div>
                    ))}
                    <p className="text-[11px] text-[#4a5568] mt-1">
                      con pratiche collegate: <b>{c.con_pratiche.length ? c.con_pratiche.join(" · ") : "nessuno"}</b>
                    </p>
                  </li>
                ))}
              </ul>
            </div>
          )}

          <div>
            <h4 className="text-sm font-semibold uppercase tracking-wide text-[#1A2B4A] mb-2">Tutti i tipi ({diag.tipi.length})</h4>
            <div className="overflow-x-auto">
              <table className="w-full" data-testid="diagnosi-tipi-table">
                <thead><tr className="border-b border-[#1A2B4A]/10">
                  <th className={th}>Nome</th><th className={th}>Pratiche</th><th className={th}>Importo</th>
                  <th className={th}>Step</th><th className={th}>Creato</th><th className={th}>Stato</th>
                </tr></thead>
                <tbody>
                  {diag.tipi.map((t) => (
                    <tr key={t.id} data-testid={`diagnosi-row-${t.id}`}
                      className={`border-b border-[#1A2B4A]/5 ${t.nel_catalogo ? "" : "bg-[#fdfaf1]"}`}>
                      <td className={td}>{t.nome}</td>
                      <td className={td}>{t.pratiche}{t.pratiche_eliminate ? ` (+${t.pratiche_eliminate} eliminate)` : ""}</td>
                      <td className={td}>{t.importo_default ? `€ ${t.importo_default}` : "—"}</td>
                      <td className={td}>{t.n_step}</td>
                      <td className={td}>{(t.created_at || "").slice(0, 10)}</td>
                      <td className={td}>{t.archiviato ? "archiviato" : t.nel_catalogo ? "in catalogo" : "fuori catalogo"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}


      {prev && (
        <div className="mt-5 space-y-6">
          {prev.catalogo_mancanti?.length > 0 && (
            <div data-testid="schema-catalogo-avviso" className="rounded-md border border-[#C9A84C]/40 bg-[#fdfaf1] px-3 py-2">
              <p className="text-[12px] text-[#7a5c12] flex items-start gap-1.5">
                <AlertTriangle className="w-4 h-4 shrink-0" />
                {prev.catalogo_mancanti.length} tipi del catalogo non sono ancora presenti in questo database:
                premi «1. Aggiorna catalogo tipi» prima di eseguire la migrazione.
              </p>
            </div>
          )}
          {!prev.pronto && (
            <div data-testid="schema-blocco" className="rounded-md border border-[#9b2c2c]/25 bg-[#fdf3f3] px-3 py-2">
              <p className="text-[12px] text-[#9b2c2c] flex items-start gap-1.5">
                <AlertTriangle className="w-4 h-4 shrink-0" />
                {prev.non_mappate.length} pratica/e senza corrispondenza esatta: restano in attesa (nessuna interpretazione automatica) finché non le assegni a mano qui sotto. Le altre righe vengono migrate normalmente.
              </p>
            </div>
          )}

          <div>
            <h4 className="text-sm font-semibold uppercase tracking-wide text-[#1A2B4A] mb-2">Tipi pratica ({prev.tipi.length})</h4>
            <div className="overflow-x-auto">
              <table className="w-full" data-testid="schema-tipi-table">
                <thead><tr className="border-b border-[#1A2B4A]/10">
                  <th className={th}>Tipo</th><th className={th}>Rinomina da</th><th className={th}>Step</th><th className={th}>Importo</th><th className={th}>Chiusure</th>
                </tr></thead>
                <tbody>
                  {prev.tipi.map((t) => (
                    <tr key={t.nome} className="border-b border-[#1A2B4A]/5">
                      <td className={td}>{t.nome}</td>
                      <td className={`${td} text-[#8a93a6]`}>{t.rinomina_da || "nuovo"}</td>
                      <td className={td}>{t.n_step}</td>
                      <td className={td}>{t.importo_label}</td>
                      <td className={td}>{t.chiude_bene ? "ok" : "da correggere"}{t.maiuscole_ok ? "" : " · maiuscole"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          <div>
            <h4 className="text-sm font-semibold uppercase tracking-wide text-[#1A2B4A] mb-2">Pratiche ({prev.righe.length})</h4>
            <div className="overflow-x-auto">
              <table className="w-full" data-testid="schema-righe-table">
                <thead><tr className="border-b border-[#1A2B4A]/10">
                  <th className={th}>Cliente</th><th className={th}>Vecchio tipo · step</th><th className={th}>Nuovo tipo · step</th><th className={th}>Stato</th>
                </tr></thead>
                <tbody>
                  {prev.righe.map((r) => (
                    <tr key={r.pratica_id} className={`border-b border-[#1A2B4A]/5 ${r.non_mappato ? "bg-[#fdf3f3]" : ""}`}>
                      <td className={td}>{r.cliente || "—"}</td>
                      <td className={td}>{r.vecchio_tipo} · <i>{r.vecchio_step}</i></td>
                      <td className={td}>
                        {r.non_mappato ? (
                          <div className="space-y-1.5">
                            <b className="text-[#9b2c2c] block">non mappato</b>
                            <select data-testid={`man-tipo-${r.pratica_id}`} value={(manuali[r.pratica_id] || {}).tipo || ""}
                              onChange={(e) => setManuali({ ...manuali, [r.pratica_id]: { tipo: e.target.value, step: "" } })}
                              className="text-xs bg-white border border-[#1A2B4A]/15 rounded-md px-2 py-1 w-full max-w-[220px]">
                              <option value="">— nuovo tipo —</option>
                              {prev.tipi.map((t) => <option key={t.nome} value={t.nome}>{t.nome}</option>)}
                            </select>
                            <select data-testid={`man-step-${r.pratica_id}`} value={(manuali[r.pratica_id] || {}).step || ""}
                              disabled={!(manuali[r.pratica_id] || {}).tipo}
                              onChange={(e) => setManuali({ ...manuali, [r.pratica_id]: { ...(manuali[r.pratica_id] || {}), step: e.target.value } })}
                              className="text-xs bg-white border border-[#1A2B4A]/15 rounded-md px-2 py-1 w-full max-w-[220px] disabled:opacity-50">
                              <option value="">— step attuale —</option>
                              {(prev.tipi.find((t) => t.nome === (manuali[r.pratica_id] || {}).tipo)?.steps || []).map((s) => (
                                <option key={s} value={s}>{s}</option>
                              ))}
                            </select>
                            <button data-testid={`man-applica-${r.pratica_id}`} onClick={() => applicaManuale(r)} disabled={!!busy}
                              className="text-xs bg-[#1A2B4A] text-white px-3 py-1 rounded-md hover:bg-[#22345c] disabled:opacity-60">
                              {busy === r.pratica_id ? "…" : "Applica"}
                            </button>
                          </div>
                        ) : (<>{r.nuovo_tipo} · <i>{r.nuovo_step}</i></>)}
                      </td>
                      <td className={td}>{r.stato || "—"}{r.motivo_archiviazione ? ` (${r.motivo_archiviazione})` : ""}{r.gia_migrata ? " · già migrata" : ""}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          <div>
            <h4 className="text-sm font-semibold uppercase tracking-wide text-[#1A2B4A] mb-2">
              Pratica Flussi — assegnazione manuale ({prev.flussi.length})
            </h4>
            {prev.flussi.length === 0 ? (
              <p className="text-[12px] text-[#4a5568]">Nessuna pratica Flussi da assegnare.</p>
            ) : (
              <ul className="space-y-2" data-testid="schema-flussi-list">
                {prev.flussi.map((r) => (
                  <li key={r.pratica_id} data-testid={`flussi-row-${r.pratica_id}`}
                    className="flex flex-wrap items-center gap-2 border border-[#1A2B4A]/10 rounded-md px-3 py-2">
                    <span className="text-[12px] text-[#1A2B4A] min-w-0 flex-1">
                      {r.cliente || "—"} · step attuale: <i>{r.vecchio_step}</i>
                    </span>
                    <select data-testid={`flussi-variante-${r.pratica_id}`} value={varianti[r.pratica_id] || "impresa"}
                      onChange={(e) => setVarianti({ ...varianti, [r.pratica_id]: e.target.value })}
                      className="text-xs bg-white border border-[#1A2B4A]/15 rounded-md px-2 py-1.5">
                      <option value="impresa">Impresa</option>
                      <option value="lavoratore">Lavoratore</option>
                    </select>
                    <button data-testid={`flussi-applica-${r.pratica_id}`} onClick={() => applicaFlussi(r)} disabled={!!busy}
                      className="text-xs bg-[#1A2B4A] text-white px-3 py-1.5 rounded-md hover:bg-[#22345c] disabled:opacity-60">
                      {busy === r.pratica_id ? "…" : "Applica"}
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>

          <div>
            <h4 className="text-sm font-semibold uppercase tracking-wide text-[#1A2B4A] mb-2">
              Visto nazionale — tipo D · scelta della variante ({(prev.visto_d || []).length})
            </h4>
            <p className="text-[11px] text-[#4a5568] mb-2">
              Il tipo vecchio non registra il canale: la variante non è deducibile e la scegli tu.
            </p>
            {(prev.visto_d || []).length === 0 ? (
              <p className="text-[12px] text-[#4a5568]">Nessuna pratica da scorporare.</p>
            ) : (
              <ul className="space-y-2" data-testid="schema-visto-d-list">
                {prev.visto_d.map((r) => (
                  <li key={r.pratica_id} data-testid={`visto-d-row-${r.pratica_id}`}
                    className="flex flex-wrap items-center gap-2 border border-[#1A2B4A]/10 rounded-md px-3 py-2">
                    <span className="text-[12px] text-[#1A2B4A] min-w-0 flex-1">
                      {r.cliente || "—"} · step attuale: <i>{r.vecchio_step}</i>
                    </span>
                    <select data-testid={`visto-d-variante-${r.pratica_id}`}
                      value={varianti[r.pratica_id] || (prev.varianti_visto_d || [])[0]}
                      onChange={(e) => setVarianti({ ...varianti, [r.pratica_id]: e.target.value })}
                      className="text-xs bg-white border border-[#1A2B4A]/15 rounded-md px-2 py-1.5">
                      {(prev.varianti_visto_d || []).map((v) => <option key={v} value={v}>{v}</option>)}
                    </select>
                    <button data-testid={`visto-d-applica-${r.pratica_id}`} onClick={() => applicaVistoD(r)} disabled={!!busy}
                      className="text-xs bg-[#1A2B4A] text-white px-3 py-1.5 rounded-md hover:bg-[#22345c] disabled:opacity-60">
                      {busy === r.pratica_id ? "…" : "Applica"}
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>


          <div>
            <div className="flex flex-wrap items-center justify-between gap-2 mb-2">
              <h4 className="text-sm font-semibold uppercase tracking-wide text-[#1A2B4A]">
                Tipi vecchi da archiviare ({(prev.tipi_da_archiviare || []).filter((t) => !t.archiviato).length})
              </h4>
              {(prev.tipi_da_archiviare || []).some((t) => !t.archiviato) && (
                <button data-testid="archivia-tutti" onClick={archiviaTutti} disabled={!!busy}
                  className="inline-flex items-center gap-1.5 text-xs border border-[#1A2B4A] px-3 py-1.5 rounded-md hover:bg-[#f9f8f5] disabled:opacity-60">
                  <Archive className="w-3.5 h-3.5" />
                  {busy === "arch" ? "…" : `Archivia tutti (${(prev.tipi_da_archiviare || []).filter((t) => !t.archiviato).length})`}
                </button>
              )}
            </div>
            {(prev.tipi_da_archiviare || []).length === 0 ? (
              <p className="text-[12px] text-[#4a5568]">Nessun tipo fuori catalogo: tutto allineato.</p>
            ) : (
              <ul className="space-y-2" data-testid="schema-archivio-list">
                {prev.tipi_da_archiviare.map((t) => (
                  <li key={t.id} data-testid={`archivia-row-${t.id}`}
                    className="flex flex-wrap items-center gap-2 border border-[#1A2B4A]/10 rounded-md px-3 py-2">
                    <span className="text-[12px] text-[#1A2B4A] min-w-0 flex-1">
                      {t.nome} · {t.pratiche === 0 ? "nessuna pratica collegata"
                        : `${t.pratiche} pratic${t.pratiche === 1 ? "a" : "he"} ancora collegat${t.pratiche === 1 ? "a" : "e"}`}
                      {t.archiviato ? " · già archiviato" : ""}
                    </span>
                    {t.archiviato ? (
                      <button data-testid={`ripristina-tipo-${t.id}`} onClick={() => archivia(t, false)} disabled={!!busy}
                        className="text-xs border border-[#1A2B4A]/25 px-3 py-1.5 rounded-md hover:bg-[#f9f8f5] disabled:opacity-60">
                        Ripristina
                      </button>
                    ) : (
                      <button data-testid={`archivia-tipo-${t.id}`} onClick={() => archivia(t, true)}
                        disabled={!!busy}
                        title={t.pratiche > 0 ? "Ha pratiche collegate: verrà chiesta conferma" : "Archivia (non elimina)"}
                        className="inline-flex items-center gap-1.5 text-xs bg-[#1A2B4A] text-white px-3 py-1.5 rounded-md hover:bg-[#22345c] disabled:opacity-40">                        <Archive className="w-3.5 h-3.5" /> {busy === t.id ? "…" : "Archivia"}
                      </button>
                    )}
                  </li>
                ))}
              </ul>
            )}
            <p className="text-[11px] text-[#8a93a6] mt-2">
              I tipi archiviati restano nel database (nessuna cancellazione) ma non vengono più proposti nei menu.
            </p>
          </div>
        </div>
      )}
    </div>
  );
}
